import React, { useEffect, useState } from "react";
import { Eye, EyeOff, TrendingUp, Zap } from "lucide-react";
import api from "../../lib/api";

function fmtInr(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (isNaN(num)) return "—";
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(1)}Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)}L`;
  return `₹${num.toLocaleString("en-IN")}`;
}

/**
 * InvestorSignalPreview — a live "what the investor sees" card that mirrors
 * the marketplace card layout. Includes a visibility indicator that tells
 * the founder whether their file is currently discoverable.
 */
export default function InvestorSignalPreview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/startup/investor-preview");
        if (!cancel) setData(res.data);
      } catch (e) { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!data) return null;
  const c = data.card || {};
  const visible = data.visible;

  return (
    <div
      className="rounded-xl border border-navy/10 bg-white shadow-sm overflow-hidden mb-6"
      data-testid="investor-signal-preview"
    >
      <div className="px-5 py-3 border-b border-navy/10 bg-emerald-50/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={12} className="text-emerald-700" />
          <div className="overline text-emerald-700">Investor Signal Preview</div>
        </div>
        <div
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
            visible
              ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
              : "bg-amber-50 text-amber-800 border border-amber-300"
          }`}
          data-testid="investor-signal-visibility"
        >
          {visible ? <><Eye size={10} className="inline mr-1" />Visible</> : <><EyeOff size={10} className="inline mr-1" />Not yet visible</>}
        </div>
      </div>

      <div className="p-5">
        <div className="text-xs text-ink-muted mb-3">
          This is exactly how your file appears on the investor marketplace right now.
        </div>

        {/* Marketplace-style mini-card */}
        <div className="border border-navy/10 rounded-lg p-4 bg-gradient-to-br from-white to-emerald-50/30 mb-4" data-testid="investor-signal-card">
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-1">
              <div className="font-heading text-ink text-base truncate" data-testid="preview-startup-name">
                {c.startup_name}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
                {c.sector || "—"} · {c.business_stage || "—"}{c.city ? ` · ${c.city}` : ""}
              </div>
            </div>
            {c.trust_score != null && (
              <div className="flex-shrink-0 ml-3 text-right">
                <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Trust</div>
                <div className={`font-heading text-lg ${
                  (c.trust_score >= 70) ? "text-emerald-700"
                    : (c.trust_score >= 40) ? "text-amber-600"
                    : "text-rose-600"
                }`} data-testid="preview-trust-score">
                  {c.trust_score}
                </div>
              </div>
            )}
          </div>

          {c.one_line_pitch ? (
            <div className="text-sm text-ink-soft leading-relaxed line-clamp-2 mb-3" data-testid="preview-pitch">
              {c.one_line_pitch}
            </div>
          ) : (
            <div className="text-xs text-amber-700 italic mb-3">Add a one-line pitch — it appears right here.</div>
          )}

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Ask</div>
              <div className="text-ink font-heading" data-testid="preview-ask">{fmtInr(c.amount_required)}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Min Ticket</div>
              <div className="text-ink font-heading">{fmtInr(c.minimum_ticket_size)}</div>
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">Revenue</div>
              <div className="text-ink font-heading">{fmtInr(c.annual_revenue)}</div>
            </div>
          </div>
        </div>

        {/* Signal strip */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-ink-muted">
            <TrendingUp size={12} className="text-emerald-600" />
            {data.signal?.match_count != null ? (
              <span><span className="text-emerald-700 font-heading">{data.signal.match_count}</span> matched investors right now</span>
            ) : (
              <span>Match preview unavailable</span>
            )}
          </div>
        </div>

        {!visible && data.visibility_reasons?.length > 0 && (
          <div className="mt-4 border-t border-navy/10 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1.5">Why not visible</div>
            <ul className="space-y-1 text-xs text-ink-soft">
              {data.visibility_reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-1.5"><span className="text-amber-500">•</span>{r}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
